/** Wiki templates: the fixed skeleton of sections a project's compiled wiki starts from, and the compile mode preference */

export const WIKI_TEMPLATE_IDS = ['software', 'research', 'personal', 'custom'] as const
export type WikiTemplateId = (typeof WIKI_TEMPLATE_IDS)[number]
export const DEFAULT_WIKI_TEMPLATE: WikiTemplateId = 'custom'

/** One fixed section of a template; compiled pages that belong to it carry its title as their topic */
export interface WikiTemplateSection {
  slug: string
  title: string
  titleVi: string
  purpose: string
  purposeVi: string
}

export interface WikiTemplate {
  id: WikiTemplateId
  sections: readonly WikiTemplateSection[]
}

export const WIKI_TEMPLATES: Readonly<Record<WikiTemplateId, WikiTemplate>> = {
  software: {
    id: 'software',
    sections: [
      { slug: 'overview', title: 'Overview', titleVi: 'Tổng quan', purpose: 'What the system does, who uses it, and how the parts fit together', purposeVi: 'Hệ thống làm gì, ai dùng, các phần ghép với nhau ra sao' },
      { slug: 'architecture', title: 'Architecture', titleVi: 'Kiến trúc', purpose: 'Components, data flow, storage, and deployment', purposeVi: 'Thành phần, luồng dữ liệu, lưu trữ và triển khai' },
      { slug: 'modules', title: 'Modules', titleVi: 'Mô-đun', purpose: 'One page per package, service, or module and what it owns', purposeVi: 'Mỗi gói, dịch vụ hoặc mô-đun một trang, kèm phần nó phụ trách' },
      { slug: 'apis-interfaces', title: 'APIs & interfaces', titleVi: 'API & giao diện', purpose: 'Endpoints, commands, events, and contracts between parts', purposeVi: 'Endpoint, lệnh, sự kiện và hợp đồng giữa các phần' },
      { slug: 'decisions', title: 'Decisions (ADR)', titleVi: 'Quyết định (ADR)', purpose: 'Decisions made, the options weighed, and why', purposeVi: 'Các quyết định đã chọn, phương án đã cân nhắc và lý do' },
      { slug: 'runbooks', title: 'Runbooks', titleVi: 'Hướng dẫn vận hành', purpose: 'Step-by-step procedures: setup, deploy, incidents, maintenance', purposeVi: 'Quy trình từng bước: cài đặt, triển khai, sự cố, bảo trì' },
      { slug: 'glossary', title: 'Glossary', titleVi: 'Thuật ngữ', purpose: 'Domain terms and abbreviations with their meaning', purposeVi: 'Thuật ngữ và từ viết tắt của lĩnh vực cùng nghĩa của chúng' },
    ],
  },
  research: {
    id: 'research',
    sections: [
      { slug: 'question-scope', title: 'Question & scope', titleVi: 'Câu hỏi & phạm vi', purpose: 'The research question, goals, and what is in or out of scope', purposeVi: 'Câu hỏi nghiên cứu, mục tiêu và những gì nằm trong hay ngoài phạm vi' },
      { slug: 'background', title: 'Background', titleVi: 'Bối cảnh', purpose: 'Context and prior work needed to follow the research', purposeVi: 'Bối cảnh và công trình trước đó cần biết để theo dõi nghiên cứu' },
      { slug: 'findings', title: 'Findings', titleVi: 'Kết quả', purpose: 'What was found, with the evidence for each finding', purposeVi: 'Những gì đã tìm ra, kèm bằng chứng cho từng kết quả' },
      { slug: 'methods', title: 'Methods', titleVi: 'Phương pháp', purpose: 'How the research was done: data, tools, procedures', purposeVi: 'Cách thực hiện nghiên cứu: dữ liệu, công cụ, quy trình' },
      { slug: 'sources-reviewed', title: 'Sources reviewed', titleVi: 'Tài liệu đã xem', purpose: 'Papers, articles, and datasets reviewed, with a short note on each', purposeVi: 'Bài báo, bài viết và bộ dữ liệu đã xem, mỗi mục kèm ghi chú ngắn' },
      { slug: 'open-questions', title: 'Open questions', titleVi: 'Câu hỏi còn mở', purpose: 'What is still unknown or disputed, and next steps', purposeVi: 'Những gì còn chưa rõ hoặc còn tranh cãi, và bước tiếp theo' },
    ],
  },
  personal: {
    id: 'personal',
    sections: [
      { slug: 'notes', title: 'Notes', titleVi: 'Ghi chú', purpose: 'Facts and notes worth keeping', purposeVi: 'Thông tin và ghi chú đáng giữ lại' },
      { slug: 'plans', title: 'Plans', titleVi: 'Kế hoạch', purpose: 'Goals, plans, and to-dos', purposeVi: 'Mục tiêu, kế hoạch và việc cần làm' },
      { slug: 'ideas', title: 'Ideas', titleVi: 'Ý tưởng', purpose: 'Ideas to explore later', purposeVi: 'Ý tưởng để khám phá sau' },
      { slug: 'reading', title: 'Reading', titleVi: 'Đọc', purpose: 'Books and articles read, with takeaways', purposeVi: 'Sách và bài viết đã đọc, kèm điều rút ra' },
      { slug: 'journal', title: 'Journal', titleVi: 'Nhật ký', purpose: 'Dated reflections and events', purposeVi: 'Suy ngẫm và sự kiện theo ngày' },
    ],
  },
  custom: { id: 'custom', sections: [] },
}

export function isWikiTemplateId(value: unknown): value is WikiTemplateId {
  return typeof value === 'string' && (WIKI_TEMPLATE_IDS as readonly string[]).includes(value)
}

/** Sections of a template; unknown or missing ids fall back to Custom (no fixed sections) */
export function wikiTemplateSections(id: string | null | undefined): readonly WikiTemplateSection[] {
  return isWikiTemplateId(id) ? WIKI_TEMPLATES[id].sections : []
}

function normalizeTopic(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[đĐ]/g, 'd').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
}

/** The template section a page topic belongs to: its slug, English title, or Vietnamese title, ignoring case and accents */
export function matchWikiTemplateSection(sections: readonly WikiTemplateSection[], topic: string | null | undefined): WikiTemplateSection | null {
  if (!topic) return null
  const key = normalizeTopic(topic)
  if (!key) return null
  return sections.find((s) => [s.slug, s.title, s.titleVi].some((v) => normalizeTopic(v) === key)) ?? null
}

/**
 * Compiler guidance for a template: place pages under the fixed sections when they fit, otherwise free topics.
 * Shared by the server compile plan prompt and the client compile brief; empty for Custom.
 */
export function wikiTemplateGuidance(id: string | null | undefined): string {
  const sections = wikiTemplateSections(id)
  if (sections.length === 0) return ''
  return [
    `This wiki follows the "${id}" template. Its fixed sections, in order:`,
    ...sections.map((s) => `- ${s.title} (Vietnamese: ${s.titleVi}): ${s.purpose}`),
    'When a page fits one of these sections, set its topic to that section title exactly (the English title, or the Vietnamese one when the Sources are in Vietnamese). When it fits none of them, use a short free topic label instead. Do not create empty pages just to fill a section.',
  ].join('\n')
}

/** Where the wiki is compiled by default: in the user's own agent (free) or on AgentWiki (credits) */
export const COMPILE_MODES = ['agent', 'server'] as const
export type CompileMode = (typeof COMPILE_MODES)[number]
export const DEFAULT_COMPILE_MODE: CompileMode = 'agent'

/** Read the `compile_mode` preference; anything else means the default */
export function parseCompileMode(raw: string | null | undefined): CompileMode {
  return raw === 'server' || raw === 'agent' ? raw : DEFAULT_COMPILE_MODE
}
